import { Helper } from "./helper.js";
import { CommandManager } from "./commandManager.js";
import { Config } from "../config/config.js";
import { DropDownListField, DropDownListProps } from "../components/dropDownList.js";
import { ComboBoxField, ComboBoxProps } from "../components/comboBox.js";
import { MultiSelectField, MultiSelectProps } from "../components/multiSelect.js";
import { MultipleCheckBoxesProps } from "../components/multipleCheckBoxes.js";
import { ListBoxSelectField, ListBoxSelectProps } from "../components/listBox.js";
import { MultipleRadioButtonsProps } from "../components/multipleRadioButtons.js";
import { NodaPeriodeNumericTextBoxField, NodaPeriodeNumericTextBoxProps } from "../components/nodaPeriodeNumericTextBox.js";
import { DateTimePickerProps } from "../components/dateTimePicker.js";
import { TextBoxField, TextBoxProps } from "../components/textBox.js";
import { CheckBoxProps } from "../components/checkBox.js";
import { NumericTextBoxField, NumericTextBoxProps } from "../components/isNumericTextBox.js";
import { TranslationTextBoxProps } from "../components/translationTextBox.js";
import { DatePickerProps } from "../components/datePicker.js";
import { IsPanelbarDetailsField, IsPanelbarDetailsProps } from "../components/isPanelbarDetails.js";
import { SortableListProps } from "../components/sortableList.js";
import { DynamicGridProps } from "../components/dynamicGrid.js";
import { IsSearchFieldField, IsSearchFieldProps } from "../components/isSearchField.js";
import { LabelField, LabelProps } from "../components/label.js";
import { GetNextNumberField, GetNextNumberProps } from "../components/getNextNumber.js";
import { IsGridField, IsGridProps } from "../components/isGrid.js";
import { TimePickerProps } from "../components/timePicker.js";
import { MultipleCheckBoxesField, TimeTextBoxField, TimeTextBoxProps } from "../components/isTimeTextBox.js";
import { ColorPickerProps } from "../components/colorPicker.js";
import { HtmlEditorProps } from "../components/htmlEditor.js";
import { SliderField, SliderProps } from "../components/slider.js";
import { BaseDataSourceField, BaseField } from "../components/baseComponentProps.js";

export class ComponentHelper {
    public static createFactory = () => {
        return function () {
            const _getBaseField = (type: string = null): BaseField => {
                const baseField: BaseField = {
                    type: type || "n/a",
                    model: "n/a",
                    name: "n/a",
                    readOnly: false,
                    visible: true,
                    required: false,
                    labelAndControlCombined: false,
                    visibleCondition: null,
                    leftContainerStyle: null,
                    rightContainerStyle: null,
                    defaultValue: null,
                    isKey: false,
                    dataSource: null,
                    title: null
                };
                return baseField;
            }

            const _setDefaultsForDataSourceField = (field: BaseDataSourceField) => {
                field.dataTextField = "Text";
                field.dataValueField = "Value";
                field.dataSourceUrl = null;
            }

            const _warnOnNoCallback = (onEvent: string, type: string) => {
                console.warn(`Default '${onEvent}' callback for '${type}' not implemented.`);
            }

            const _createOnChange = (type: string) => {
                return (field, value, dataItem) => {
                    _warnOnNoCallback('onChange', type);
                }
            }

            const _createOnDataLoaded = (type: string) => {
                return (field, model) => {
                    _warnOnNoCallback('onDataLoaded', type);
                }
            }

            const _createHandleEval = (type: string) => {
                return (action, prepare) => {
                    _warnOnNoCallback("handleEval", type);
                }
            }

            return {
                //non-kendo
                getDefaultTextBoxProps: (): TextBoxProps => {
                    const field = _getBaseField("textbox");
                    const tbField = (field as TextBoxField);
                    tbField.isPassword = false;
                    tbField.maxLength = null;
                    tbField.multiLine = false;
                    tbField.triggerOnChange = false;

                    const props: TextBoxProps = {
                        field: tbField,
                        disabled: false,
                        required: false,
                        value: "",
                        onChange: _createOnChange('TextBoxProps')
                    };
                    return props;
                },
                getDefaultTranslationTextBoxProps: (): TranslationTextBoxProps => {
                    const field = _getBaseField("translationtextbox");
                    const props: TranslationTextBoxProps = {
                        field: field,
                        disabled: false,
                        required: false,
                        value: null,
                        multiLine: false,
                        onChange: _createOnChange('TranslationTextBoxProps'),
                        language: "language",
                        translation: "translation"
                    };
                    return props;
                },
                getDefaultCheckBoxProps: (): CheckBoxProps => {
                    const props: CheckBoxProps = {
                        field: _getBaseField("checkbox"),
                        disabled: false,
                        required: false,
                        value: "",
                        onChange: _createOnChange('CheckBoxProps')
                    };
                    return props;
                },
                getDefaultMultipleRadioButtonsProps: (): MultipleRadioButtonsProps => {
                    const field = _getBaseField("is-multiple-radio-buttons");
                    const mrbField = (field as MultipleCheckBoxesField);
                    _setDefaultsForDataSourceField(mrbField);

                    const props: MultipleRadioButtonsProps = {
                        field: mrbField,
                        disabled: false,
                        required: false,
                        value: null,
                        onChange: _createOnChange('MultipleRadioButtonsProps'),
                        dataSource: null,
                        //context: {}, //braucht key1...
                        onDataLoaded: _createOnDataLoaded('MultipleRadioButtonsProps')
                    };

                    return props;
                },
                getDefaultNodaPeriodNumericProps: (): NodaPeriodeNumericTextBoxProps => {
                    const field = _getBaseField("noda-periode-numeric-textboxes");
                    const nodaField = (field as NodaPeriodeNumericTextBoxField);
                    const props: NodaPeriodeNumericTextBoxProps = {
                        field: nodaField,
                        disabled: false,
                        required: false,
                        value: "",
                        onChange: _createOnChange('NodaPeriodeNumericTextBoxProps'),
                        handleEval: _createHandleEval('NodaPeriodeNumericTextBoxProps')
                    };
                    return props;
                },
                getDefaultMultipleCheckBoxesProps: (): MultipleCheckBoxesProps => {
                    const field = _getBaseField("is-multiple-check-boxes");
                    const mcbField = (field as MultipleCheckBoxesField);
                    _setDefaultsForDataSourceField(mcbField);

                    const props: MultipleCheckBoxesProps = {
                        field: mcbField,
                        disabled: false,
                        required: false,
                        value: null,
                        onChange: _createOnChange('MultipleCheckBoxesProps'),
                        dataSource: null,
                        //context: {}, //braucht key1...
                        onDataLoaded: _createOnDataLoaded('MultipleCheckBoxesProps')
                    };

                    return props;
                },
                getDefaultLabelProps: (): LabelProps => {
                    const field = _getBaseField("label");
                    const lField = (field as LabelField);
                    lField.isUrl = false;
                    lField.urlTemplate = null;
                    lField.textTemplate = null;

                    const props: LabelProps = {
                        field: lField,
                        disabled: false,
                        required: false,
                        value: "",
                        onChange: _createOnChange('LabelProps'),
                        handleEval: _createHandleEval('LabelProps')
                    };
                    return props;
                },
                getDefaultNextNumberProps: (): GetNextNumberProps => {
                    const field = _getBaseField("is-get-next-number");
                    const gnnField = (field as GetNextNumberField);
                    gnnField.url = null;

                    const props: GetNextNumberProps = {
                        field: gnnField,
                        disabled: false,
                        required: false,
                        value: "",
                        onChange: _createOnChange('GetNextNumberProps'),
                        onDataLoaded: _createOnDataLoaded('GetNextNumberProps')
                    };
                    return props;
                },
                //contains kendo
                getDefaultDateTimePickerProps: (): DateTimePickerProps => {
                    const props: DateTimePickerProps = {
                        field: _getBaseField("kendo-date-time-picker"),
                        disabled: false,
                        required: false,
                        value: null,
                        onChange: _createOnChange('DateTimePickerProps'),
                        handleEval: _createHandleEval('DateTimePickerProps')
                    };
                    return props;
                },
                getDefaultDatePickerProps: (): DatePickerProps => {
                    const props: DatePickerProps = {
                        field: _getBaseField("kendo-date-picker"),
                        disabled: false,
                        required: false,
                        value: null,
                        onChange: _createOnChange('DatePickerProps')
                    };
                    return props;
                },
                getDefaultTimePickerProps: (): TimePickerProps => {
                    const props: TimePickerProps = {
                        field: _getBaseField("kendo-time-picker"),
                        disabled: false,
                        required: false,
                        value: null,
                        onChange: _createOnChange('TimePickerProps')
                    };
                    return props;
                },
                getDefaultDropDownProps: (): DropDownListProps => {
                    const field = _getBaseField("kendo-drop-down-list");
                    const ddlField = (field as DropDownListField);
                    _setDefaultsForDataSourceField(ddlField);
                    ddlField.listWidth = 450;
                    ddlField.optionLabel = Config.translations.pleaseSelect;
                    ddlField.template = null;

                    const props: DropDownListProps = {
                        field: ddlField,
                        disabled: false,
                        required: false,
                        value: null,
                        onChange: _createOnChange('DropDownListProps'),
                        commandManager: null,
                        dataSource: null,
                        //context: {}, //braucht key1...
                        onDataLoaded: _createOnDataLoaded('DropDownListProps')
                    };
                    return props;
                },
                getDefaultListBoxProps: (): ListBoxSelectProps => {
                    const field = _getBaseField("kendo-list-box");
                    const ddlField = (field as ListBoxSelectField);
                    _setDefaultsForDataSourceField(ddlField);
                    ddlField.maxSelectedItems = null;
                    ddlField.height = "400px";
                    ddlField.template = null;
                    ddlField.labelTextLeft = null;
                    ddlField.labelTextRight = null;

                    const props: ListBoxSelectProps = {
                        field: ddlField,
                        disabled: false,
                        required: false,
                        value: null,
                        onChange: _createOnChange('ListBoxSelectProps'),
                        dataSource: null,
                        //context: {}, //braucht key1...
                        onDataLoaded: _createOnDataLoaded('ListBoxSelectProps')
                    };

                    return props;
                },
                getDefaultComboBoxProps: (): ComboBoxProps => {
                    const field = _getBaseField("kendo-combo-box");
                    const cbField = (field as ComboBoxField);
                    _setDefaultsForDataSourceField(cbField);
                    cbField.listWidth = 450;

                    const props: ComboBoxProps = {
                        field: cbField,
                        disabled: false,
                        required: false,
                        value: null,
                        onChange: _createOnChange('ComboBoxProps'),
                        commandManager: null,
                        dataSource: null,
                        //context: {}, //braucht key1...
                        onDataLoaded: _createOnDataLoaded('ComboBoxProps')
                    };
                    return props;
                },
                getDefaultMultiSelectProps: (): MultiSelectProps => {
                    const field = _getBaseField("kendo-multi-select");
                    const msField = (field as MultiSelectField);
                    msField.listWidth = 450;
                    msField.primitiveValue = false;
                    msField.displayTemplate = null;

                    const props: MultiSelectProps = {
                        field: msField,
                        disabled: false,
                        required: false,
                        value: null,
                        onChange: _createOnChange('MultiSelectProps'),
                        dataSource: null,
                        onDataLoaded: _createOnDataLoaded('MultiSelectProps'),
                        commandManager: null
                    };
                    return props;
                },
                getDefaultSearchFieldProps: (): IsSearchFieldProps => {
                    const field = _getBaseField("is-search-field");
                    const issField = (field as IsSearchFieldField);
                    issField.listWidth = 450;
                    issField.autocompleteAdditionalFilterParameter = null;
                    issField.autocompleteAdditionalFilterParameterName = null;
                    issField.autocompleteDataTextField = "text";
                    issField.autocompleteEnabled = false;
                    issField.autocompleteTemplate = null;
                    issField.autocompleteUrl = null;
                    issField.customButtonEnabled = false;
                    issField.customButtonFunction = null;
                    issField.customButtonTitle = null;
                    issField.dataTextField = "text";
                    issField.dataValueField = "value";
                    issField.deleteButtonEnabled = false;
                    issField.deleteButtonTitle = null;
                    issField.fullObjectKey = null;
                    issField.listWidth = 450;
                    issField.readOnlyTextField = false;
                    issField.searchButtonEnabled = true;
                    issField.searchTextField = null;
                    issField.searchValueField = null;
                    issField.searchWindowTitle = null;
                    issField.searchWindowUrl = null;
                    issField.searchWindowUrlAdditionalParameterModel = null;
                    issField.searchWindowUrlAdditionalParameterName = null;
                    issField.searchWindowUrlParameterModel = null;
                    issField.searchWindowUrlParameterName = null;
                    issField.newButtonEnabled = false;
                    issField.addNewUrl = null;
                    issField.newObjectKey = null;
                    issField.newObjectWindowTitle = null;
                    issField.addNewUrlParameters = null;
                    issField.canOpenAdd = false;

                    const props: IsSearchFieldProps = {
                        field: issField,
                        disabled: false,
                        required: false,
                        value: null,
                        onChange: _createOnChange('IsSearchFieldProps'),
                        //context: {}, //braucht key1...
                        handleEval: _createHandleEval('IsSearchFieldProps'),
                        handleIsSearchFieldFullObjectModel: (field, object) => {
                            _warnOnNoCallback("handleIsSearchFieldFullObjectModel", 'IsSearchFieldProps');
                        },
                        multiSelectMode: false,
                        commandManager: null
                    };

                    return props;
                },
                getDefaultPanelbarDetailsProps: (): IsPanelbarDetailsProps => {
                    const field = _getBaseField("is-panelbar-details");
                    const pbdField = (field as IsPanelbarDetailsField);
                    pbdField.url = null;
                    pbdField.template = null;

                    const props: IsPanelbarDetailsProps = {
                        field: pbdField,
                        disabled: false,
                        required: false,
                        value: null,
                        onChange: _createOnChange('IsPanelbarDetailsProps'),
                        commandManager: null,
                        onDataLoaded: _createOnDataLoaded('IsPanelbarDetailsProps'),
                        handleEval: _createHandleEval('IsPanelbarDetailsProps')
                    };
                    return props;
                },
                getDefaultNumericTextBoxProps: (): NumericTextBoxProps => {
                    const field = _getBaseField("is-numeric-text-box");
                    const ntbField = (field as NumericTextBoxField);
                    ntbField.min = null;
                    ntbField.max = null;
                    ntbField.spinners = true;
                    ntbField.format = "#.##";
                    ntbField.decimals = 2;
                    ntbField.modelUnity = null;

                    const props: NumericTextBoxProps = {
                        field: ntbField,
                        disabled: false,
                        required: false,
                        value: null,
                        onChange: _createOnChange('NumericTextBoxProps'),
                        //context: {}, //braucht key1...
                        handleEval: _createHandleEval('NumericTextBoxProps')
                    };

                    return props;
                },
                getDefaultIsGridProps: (): IsGridProps => {
                    const field = _getBaseField("is-grid");
                    const isgField = (field as IsGridField);
                    isgField.gridOptions = null;
                    isgField.schema = null;
                    isgField.pageSize = 20;
                    isgField.map = null;
                    isgField.searchButtonEnabled = false;
                    isgField.searchWindowTitle = null;
                    isgField.searchWindowUrl = null;
                    isgField.style = null;

                    const props: IsGridProps = {
                        field: isgField,
                        disabled: false,
                        required: false,
                        value: null,
                        onChange: _createOnChange('IsGridProps'),
                        //context: {}, //braucht key1...
                    };

                    return props;
                },
                getDefaultTimeTextBoxProps: (): TimeTextBoxProps => {
                    const field = _getBaseField("is-time-text-box");
                    const ttbField = (field as TimeTextBoxField);
                    ttbField.modelForTimeString = null;

                    const props: TimeTextBoxProps = {
                        field: ttbField,
                        disabled: false,
                        required: false,
                        value: null,
                        onChange: _createOnChange('TimeTextBoxProps'),
                        //context: {}, //braucht key1...
                    };

                    return props;
                },
                getDefaultColorPickerProps: (): ColorPickerProps => {
                    const props: DatePickerProps = {
                        field: _getBaseField("kendo-color-picker"),
                        disabled: false,
                        required: false,
                        value: null,
                        onChange: _createOnChange('ColorPickerProps')
                    };
                    return props;
                },
                getDefaultHtmlEditorProps: (): HtmlEditorProps => {
                    const props: HtmlEditorProps = {
                        field: _getBaseField("htmlEditor"),
                        disabled: false,
                        required: false,
                        value: null,
                        onChange: _createOnChange('HtmlEditorProps'),
                        commandManager: CommandManager.create()
                    };
                    return props;
                },
                getDefaultSliderProps: (): SliderProps => {
                    const field = _getBaseField("slider");
                    const sField = (field as SliderField);
                    sField.min = 0;
                    sField.max = 100;
                    sField.smallStep = 1;
                    sField.largeStep = 10;

                    const props: SliderProps = {
                        field: sField,
                        disabled: false,
                        required: false,
                        value: null,
                        onChange: _createOnChange('SliderProps')
                    };
                    return props;
                },
            }
        }();
    };

    public static getComponentByType: ComponentGenerator = (type: string): any => {
        const factory = ComponentHelper.createFactory();

        switch (type) {
            case "textbox":
                return factory.getDefaultTextBoxProps();
            case "checkbox":
                return factory.getDefaultCheckBoxProps();
            case "is-multiple-radio-buttons":
                return factory.getDefaultMultipleRadioButtonsProps();
            case "is-multiple-check-boxes":
                return factory.getDefaultMultipleCheckBoxesProps();
            case "label":
                return factory.getDefaultLabelProps();
            case "is-get-next-number":
                return factory.getDefaultNextNumberProps();
            case "kendo-date-time-picker":
                return factory.getDefaultDateTimePickerProps();
            case "kendo-date-picker":
                return factory.getDefaultDatePickerProps();
            case "kendo-time-picker":
                return factory.getDefaultTimePickerProps();
            case "kendo-drop-down-list":
                return factory.getDefaultDropDownProps();
            case "kendo-combo-box":
                return factory.getDefaultComboBoxProps();
            case "kendo-multi-select":
                return factory.getDefaultMultiSelectProps();
            case "is-search-field":
                return factory.getDefaultSearchFieldProps();
            case "is-panelbar-details":
                return factory.getDefaultPanelbarDetailsProps();
            case "is-numeric-text-box":
                return factory.getDefaultNumericTextBoxProps();
            case "is-grid":
                return factory.getDefaultIsGridProps();
            case "is-time-text-box":
                return factory.getDefaultTextBoxProps();
            case "kendo-color-picker":
                return factory.getDefaultColorPickerProps();
            case "htmlEditor":
                return factory.getDefaultHtmlEditorProps();
            default:
                return null;
        }
    }

    public static getKendoGridStandardOptions = (settings?: { useColumnStateManager?: boolean; id?: string; initial?: kendo.ui.GridOptions }) => {
        const defaultOptions: kendo.ui.GridOptions = {
            pageable: true,
            reorderable: true,
            /*not perfect... http://docs.telerik.com/kendo-ui/controls/data-management/grid/appearance#column-widths */
            resizable: true,
            scrollable: true,
            selectable: true,
            sortable: true,
        };

        const options = $.extend({}, defaultOptions, settings ? settings.initial : null);

        if ((options.reorderable || options.resizable) && settings && settings.useColumnStateManager) {
            const handleColumnChange = (ev: kendo.ui.GridColumnReorderEvent | kendo.ui.GridColumnResizeEvent, isResize: boolean) => {
                const sorting = ev.sender.dataSource.sort();
                const columns: Array<ColumnInfo> = ev.sender.columns.map(col => {
                    if (!Helper.hasValue(col.field)) {
                        console.warn(`Grid column does not have the 'field' property set (grid id = '${settings ? settings.id : null}').`);
                    }
                    const result: ColumnInfo = {
                        name: col.field,
                        width: col.width
                    };
                    if (Helper.hasValue(sorting)) {
                        const item = sorting.find(f => f.field == result.name);
                        if (Helper.hasValue(item)) {
                            result.sort = item.dir as any;
                        }
                    }
                    return result;
                });
                if (isResize) {
                    const field = ((ev as kendo.ui.GridColumnResizeEvent).column as kendo.ui.GridColumn).field;
                    const idx = Helper.findObjIndex(columns, "name", field);
                    if (idx !== -1) {
                        columns[idx].width = (ev as kendo.ui.GridColumnResizeEvent).newWidth;
                    }
                } else {
                    let nIdx = (ev as kendo.ui.GridColumnReorderEvent).newIndex, oIdx = (ev as kendo.ui.GridColumnReorderEvent).oldIndex;
                    const column = columns[oIdx];
                    columns.splice(oIdx, 1);
                    columns.splice(nIdx, 0, column);
                }
                Helper.saveDataToStorage(ComponentHelper.getKendoGridStorageKey(settings.id), columns, false);
            }

            options.columnReorder = (e) => {
                handleColumnChange(e, false);
            };
            options.columnResize = (e) => {
                handleColumnChange(e, true);
            };
            options.sort = (ev) => {
                const sorting = ev.sort;
                const columns: Array<ColumnInfo> = ev.sender.columns.map(col => {
                    if (!Helper.hasValue(col.field)) {
                        console.warn(`Grid column does not have the 'field' property set (grid id = '${settings ? settings.id : null}').`);
                    }
                    const result: ColumnInfo = {
                        name: col.field,
                        width: col.width
                    };
                    if (Helper.hasValue(sorting) && sorting.field == col.field && !Helper.isNullOrEmpty(sorting.dir)) {
                        result.sort = sorting.dir as "asc" | "desc";
                    }
                    return result;
                });
                Helper.saveDataToStorage(ComponentHelper.getKendoGridStorageKey(settings.id), columns, false);
            }
        }

        return options;
    }

    public static getKendoGridColumns = (id: string, existing?: Array<kendo.ui.GridColumn>) => {
        const columns: Array<ColumnInfo> = Helper.loadDataFromStorage(ComponentHelper.getKendoGridStorageKey(id));

        //multi column headers not supported
        if (!Array.isArray(columns) || columns.length === 0 || existing.filter(x => x.columns != null).length > 0) {
            for (let i = 0; i < existing.length; i++) {
                //LB Header Template wurde immer wieder Überschrieben, deshalb hier ein Fix
                existing[i].headerTemplate = "<span title='" + existing[i].title + "' > " + Helper.hasValue(existing[i].headerTemplate) ? existing[i].headerTemplate : existing[i].title + "</span>";
            }
            return existing;
        }

        if (!Array.isArray(existing)) {
            return [];
        }

        return ComponentHelper.orderColumns(id, columns, existing, true);
    }

    public static orderColumns = (id, localStorageColumns: Array<ColumnInfo>, existing?: Array<kendo.ui.GridColumn>, changeHeaderTemplate?: boolean): kendo.ui.GridColumn[] => {
        const ordered: Array<kendo.ui.GridColumn> = [];
        const colNames = existing.map(c => c.field).filter(Boolean);
        const considered: Array<string> = [];

        if (!Helper.hasValue(localStorageColumns))
            return existing;

        //add only saved columns, which are present in the current (existing) configuration (grid options)
        localStorageColumns.forEach(c => {
            const idx = Helper.findObjIndex(existing, "field", c.name);
            if (idx !== -1 && Helper.hasValue(c.name)) {
                const column = existing[idx];
                //columns without field/name -> tools/options => just add
                if (!Helper.hasValue(c.name) || colNames.indexOf(c.name) >= 0) {
                    column.width = c.width;
                    //LB Header Template wurde immer wieder Überschrieben, deshalb hier ein Fix 
                    if (changeHeaderTemplate)
                        column.headerTemplate = "<span title= '" + column.title + "' > " + Helper.hasValue(column.headerTemplate) ? column.headerTemplate : column.title + "</span>";

                    if (Helper.hasValue(c.columns) && c.columns.length > 0) {
                        let orderedSubColumns = ComponentHelper.orderColumns(id, c.columns, column.columns, changeHeaderTemplate);
                        column.columns = orderedSubColumns;
                    }

                    ordered.push(column);
                    considered.push(c.name);
                }
            } else {
                console.warn(`Could not find grid column: '${c.name}'. Grid ID: '${id}'.`);
            }
        });

        //check for new columns, that have not been saved yet
        existing.forEach(c => {
            if (!Helper.hasValue(c.field)) {
                ordered.push(c);
            } else {
                const idx = considered.indexOf(c.field);
                if (considered.indexOf(c.field) === -1) {
                    ordered.push(c);
                }
            }
        });

        return ordered;
    }

    public static getKendoGridStorageKey = (id: string) => {
        return `__${Config.user.userName}-grid-${id}__`;
    }
}

export interface ColumnInfo {
    name: string;
    width: number | string;
    sort?: "asc" | "desc";
    columns?: ColumnInfo[];
}

interface ComponentGenerator {
    (type: string): any;

    (type: "textbox"): TextBoxProps;
    (type: "checkbox"): CheckBoxProps;
    (type: "is-multiple-radio-buttons"): MultipleRadioButtonsProps;
    (type: "is-multiple-check-boxes"): MultipleCheckBoxesProps;
    (type: "label"): LabelProps;
    (type: "is-get-next-number"): GetNextNumberProps;
    (type: "kendo-date-time-picker"): DateTimePickerProps;
    (type: "kendo-date-picker"): DatePickerProps;
    (type: "kendo-time-picker"): TimePickerProps;
    (type: "kendo-drop-down-list"): DropDownListProps;
    (type: "kendo-combo-box"): ComboBoxProps;
    (type: "kendo-multi-select"): MultiSelectProps;
    (type: "is-search-field"): IsSearchFieldProps;
    (type: "is-panelbar-details"): IsPanelbarDetailsProps;
    (type: "is-numeric-text-box"): NumericTextBoxProps;
    (type: "is-grid"): IsGridProps;
    (type: "is-time-text-box"): TimeTextBoxProps;
    (type: "kendo-color-picker"): ColorPickerProps;
    (type: "htmlEditor"): HtmlEditorProps;
    (type: "is-sortable-list"): SortableListProps;
    (type: "is-dynamic-grid"): DynamicGridProps;
}